import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { __ } from '@wordpress/i18n';
import CptTable from './CptTable';
import { administrationColumns } from './CptTable/columns/administrations';
import { apiFetch, fetchTableRows } from '../scripts/api';
import { splitValues, downloadCsv } from '../scripts/exportCsv';

const taxNames = ( c ) => c?.nodes?.map( ( n ) => n.name ).join( '; ' ) ?? '';
const postTitles = ( c ) =>
	c?.nodes?.map( ( n ) => n.title ).join( '; ' ) ?? '';

function exportAdministrationsToCSV( rows ) {
	const headers = [
		__( 'Administration', 'anacrm' ),
		__( 'Territory', 'anacrm' ),
		__( 'Region', 'anacrm' ),
		__( 'Municipality', 'anacrm' ),
		__( 'Geographical scope', 'anacrm' ),
		__( 'Mother administration', 'anacrm' ),
		__( 'Work scope', 'anacrm' ),
		__( 'Contact person', 'anacrm' ),
		__( 'Email', 'anacrm' ),
		__( 'Phone', 'anacrm' ),
	];
	const dataRows = rows.map( ( r ) => [
		r.title ?? '',
		taxNames( r.territories ),
		taxNames( r.regions ),
		taxNames( r.cities ),
		taxNames( r.geoscopes ),
		postTitles( r.administrationUpper ),
		taxNames( r.workScopes ),
		postTitles( r.contactPersonRef ),
		r.contactMail ?? '',
		r.contactPhone ?? '',
	] );
	downloadCsv(
		headers,
		dataRows,
		`administrations-${ new Date().toISOString().slice( 0, 10 ) }.csv`
	);
}

export default function AdministrationTable() {
	const [ data, setData ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );
	const [ emailList, setEmailList ] = useState( null );
	const [ shareUrl, setShareUrl ] = useState( null );
	const [ copied, setCopied ] = useState( false );

	useEffect( () => {
		fetchTableRows( 'administration' )
			.then( ( nodes ) => setData( nodes ) )
			.catch( ( err ) => setError( err.message ) )
			.finally( () => setLoading( false ) );
	}, [] );

	const handleDelete = async ( databaseId ) => {
		try {
			await apiFetch( `/anacrm/v1/administration/${ databaseId }`, {
				method: 'DELETE',
			} );
			setData( ( prev ) =>
				prev.filter( ( item ) => item.databaseId !== databaseId )
			);
		} catch ( err ) {
			window.alert(
				__(
					'The following administration could not be deleted:',
					'anacrm'
				) + err.message
			);
		}
	};

	useEffect( () => {
		const handler = () => setShareUrl( window.location.href );
		document.addEventListener( 'anacrm:share-administration', handler );
		return () =>
			document.removeEventListener(
				'anacrm:share-administration',
				handler
			);
	}, [] );

	const handleCopyLink = useCallback( () => {
		navigator.clipboard.writeText( shareUrl ).then( () => {
			setCopied( true );
			setTimeout( () => setCopied( false ), 2000 );
		} );
	}, [ shareUrl ] );

	function handleEmailExport( rows ) {
		const emails = rows
			.map( ( r ) => splitValues( r.contactMail )[ 0 ] )
			.filter( Boolean )
			.join( ', ' );
		setEmailList( emails );
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const columns = useMemo(
		() => administrationColumns( { onDelete: handleDelete } ),
		[]
	);

	if ( error ) {
		return (
			<div className="px-4 py-3 border-2 border-red text-red text-sm">
				{ __( 'Error loading data:', 'anacrm' ) } { error }
			</div>
		);
	}

	const filters = [
		{
			columnId: 'territories',
			label: __( 'Territory', 'anacrm' ),
			getTerms: ( row ) =>
				row.territories?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'regions',
			label: __( 'Region', 'anacrm' ),
			getTerms: ( row ) =>
				row.regions?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'cities',
			label: __( 'Municipality', 'anacrm' ),
			getTerms: ( row ) =>
				row.cities?.nodes?.map( ( n ) => n.name ) ?? [],
			allowedValues: window.anacrm?.allowedArchiveCities ?? null,
			dependsOnColumnId: 'territories',
			relationMap: window.anacrm?.cityTerritoryMap ?? {},
			disabledWhenValues:
				window.anacrm?.cityFilterDisabledTerritories ?? [],
		},
		{
			columnId: 'geoscopes',
			label: __( 'Geographical scope', 'anacrm' ),
			getTerms: ( row ) =>
				row.geoscopes?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'administrationUpper',
			label: __( 'Mother administration', 'anacrm' ),
			getTerms: ( row ) =>
				row.administrationUpper?.nodes?.map( ( n ) => n.title ) ?? [],
		},
		{
			columnId: 'workScopes',
			label: __( 'Work scope', 'anacrm' ),
			getTerms: ( row ) =>
				row.workScopes?.nodes?.map( ( n ) => n.name ) ?? [],
		},
	];

	const emailContainer = document.getElementById(
		'anacrm-email-export-administration'
	);
	const shareContainer = document.getElementById(
		'anacrm-share-administration'
	);

	return (
		<>
			<CptTable
				columns={ columns }
				data={ data }
				isLoading={ loading }
				filters={ filters }
				exportEvent="anacrm:export-administration"
				onExportRows={ exportAdministrationsToCSV }
				emailExportEvent="anacrm:export-emails-administration"
				onEmailExportRows={ handleEmailExport }
				searchPlaceholder={ __( 'Search administrations…', 'anacrm' ) }
				fitWidth
			/>
			{ emailList !== null &&
				emailContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<span>{ emailList }</span>
						<button
							onClick={ () => setEmailList( null ) }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer"
						>
							&times;
						</button>
					</div>,
					emailContainer
				) }
			{ shareUrl !== null &&
				shareContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<div className="flex flex-col gap-1 min-w-0">
							<strong>{ __( 'Share', 'anacrm' ) }</strong>
							<div className="flex items-center gap-3 flex-wrap">
								<span className="text-xs">
									{ __( 'Link to share:', 'anacrm' ) }{ ' ' }
									<a
										href={ shareUrl }
										className="underline break-all"
									>
										{ shareUrl }
									</a>
								</span>
								<button
									onClick={ handleCopyLink }
									className="shrink-0 px-3 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold text-xs"
								>
									{ copied
										? __( 'Copied!', 'anacrm' )
										: __( 'Copy link', 'anacrm' ) }
								</button>
							</div>
						</div>
						<button
							onClick={ () => {
								setShareUrl( null );
								setCopied( false );
							} }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer shrink-0"
						>
							&times;
						</button>
					</div>,
					shareContainer
				) }
		</>
	);
}
